<template>
  <!-- The banner (owner's wording, 2026-10-28). English-only for now, like every other
       demo notice in the app. Above the dialog layer so it shows over a maximized dialog. -->
  <div v-if="previewActive" class="dd-preview-banner" role="status">
    <span><b>Demo venue</b> · Look around freely, changes are off</span>
    <q-btn unelevated dense no-caps color="primary" label="Start your own"
           class="dd-preview-allow q-px-sm" @click="startOwn" />
  </div>
</template>

<script setup lang="ts">
/**
 * The one guard behind preview mode — see composables/usePreviewMode.ts for the why.
 *
 *   1. TAGS: a `.q-dialog` that appears while a preview is armed (or while another preview
 *      dialog is open) gets `data-dd-preview`.
 *   2. BLOCKS: capture-phase listeners on the document stop every interaction with a field,
 *      switch or button inside a preview, before the component's own handler sees it, and
 *      explain with a toast. Allowed: close/back buttons, tabs, expansion headers, external
 *      links, scrolling, Escape, and the backdrop.
 *   3. SAFETY NET: while a preview is on screen, Parse saves/deletes from this app are refused
 *      — in case a dialog writes on its own (autosave on change, defaults on open).
 *
 * Nothing in here decides WHETHER something is a preview: only openInPreview() (called by
 * the pages for showcase venues) and a page's own `data-dd-preview` attribute do.
 */
import { onMounted, onBeforeUnmount } from 'vue'
import { useQuasar } from 'quasar'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Parse } from 'src/boot/parse'
import useAuth from 'src/composables/useAuth'
import { PREVIEW_SELECTOR, isPreviewArmed, disarmPreview, previewActive } from 'src/composables/usePreviewMode'

const $q = useQuasar()
const router = useRouter()
const { logout } = useAuth()
const { t } = useI18n({ useScope: 'global' })

const NOTICE = 'Demo venue: changes are off'

// ── 1. What may still be used inside a preview ──────────────────────────────
const CLOSE_ICONS = /(^|\s)(mdi-close|mdi-arrow-left|mdi-chevron-left|mdi-window-close)(\s|$)/
const CLOSE_TEXT = new Set(['close', 'arrow_back', 'chevron_left', 'cancel'])
const ALLOWED = '.dd-preview-allow, [data-preview-allow], .q-tab, .q-tabs__arrow, '
  + '.q-expansion-item__container > .q-item, .q-scrollarea__bar, .q-scrollarea__thumb, a[target="_blank"]'
const INTERACTIVE = 'input, textarea, select, [contenteditable="true"], [contenteditable=""], button, a, '
  + '.q-btn, .q-toggle, .q-checkbox, .q-radio, .q-field, .q-slider, .q-range, .q-item--clickable, '
  + '.q-chip--clickable, .q-rating, .q-date, .q-time, .q-color-picker, .q-uploader, .q-editor, '
  + '[role="button"], [role="switch"], [role="checkbox"], [role="radio"], [role="slider"], [role="option"]'

/** Text buttons that only close: "Close" / "Cancel" / "Back" in the visitor's language.
 *  NOT "Done" or "OK" — in some dialogs those save on the way out. */
function closeLabels(): Set<string> {
  return new Set(['general.close', 'general.cancel', 'general.back'].map((k) => t(k).trim().toLowerCase()))
}

function isCloseButton(btn: Element): boolean {
  for (const icon of btn.querySelectorAll('.q-icon')) {
    if (CLOSE_ICONS.test(icon.className) || CLOSE_TEXT.has((icon.textContent || '').trim())) return true
  }
  const label = (btn.querySelector('.q-btn__content')?.textContent || btn.textContent || '').trim().toLowerCase()
  return !!label && closeLabels().has(label)
}

/** Mark the close buttons inside a preview so CSS leaves them in colour. */
function markAllowed(root: ParentNode) {
  for (const btn of root.querySelectorAll('.q-btn')) {
    if (!btn.classList.contains('dd-preview-allow') && isCloseButton(btn)) btn.classList.add('dd-preview-allow')
  }
}

/** The element the visitor touched, if it is something preview mode must refuse. */
function blockedTarget(e: Event): Element | null {
  const el = e.target instanceof Element ? e.target : null
  if (!el || !el.closest(PREVIEW_SELECTOR)) return null
  if (el.closest('.dd-preview-banner')) return null
  const allowed = el.closest(ALLOWED)
  if (allowed) return null
  const btn = el.closest('.q-btn')
  if (btn && isCloseButton(btn)) return null
  return el.closest(INTERACTIVE)
}

// ── 2. Blocking ──────────────────────────────────────────────────────────────
let lastToast = 0
function notify() {
  if (Date.now() - lastToast < 1500) return
  lastToast = Date.now()
  // Bottom, so it never sits on top of the banner.
  $q.notify({ message: NOTICE, icon: 'mdi-eye-outline', classes: 'glass-blur text-black', position: 'bottom', timeout: 2000 })
}

function onPress(e: Event) {
  // pointerdown / mousedown / touchstart: stop drags (sliders, sortables) and focus, silently.
  // NO preventDefault on touch — that would also kill scrolling the dialog with a finger.
  if (!blockedTarget(e)) return
  e.stopPropagation()
  if (e.type === 'mousedown') e.preventDefault()
}
function onClick(e: Event) {
  if (!blockedTarget(e)) return
  e.preventDefault()
  e.stopImmediatePropagation()
  notify()
}
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape' || e.key === 'Tab') return
  const el = blockedTarget(e)
  if (!el) return
  const typing = el.matches('input, textarea, [contenteditable="true"], [contenteditable=""]') || !!el.closest('.q-field')
  if (typing || e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    e.stopImmediatePropagation()
    notify()
  }
}
function onEdit(e: Event) {
  // input / beforeinput / change / paste / drop / submit — belt and braces behind onKey.
  if (!blockedTarget(e) && !(e.type === 'submit' && (e.target as Element)?.closest?.(PREVIEW_SELECTOR))) return
  e.preventDefault()
  e.stopImmediatePropagation()
}
function onFocus(e: FocusEvent) {
  // Keeps the phone keyboard from popping up on a field the visitor can't type into.
  const el = e.target
  if (el instanceof HTMLElement && el.matches('input, textarea') && el.closest(PREVIEW_SELECTOR)) el.blur()
}

// ── 1b. Tagging dialogs as they appear ───────────────────────────────────────
function refreshActive() {
  const nodes = document.querySelectorAll(PREVIEW_SELECTOR)
  previewActive.value = nodes.length > 0
  nodes.forEach((n) => markAllowed(n))
}

function tagWhenReady(node: Element, tries = 0) {
  const dialog = node.matches('.q-dialog') ? node : node.querySelector('.q-dialog')
  if (!dialog) {
    if (tries < 10) requestAnimationFrame(() => tagWhenReady(node, tries + 1))
    return
  }
  dialog.setAttribute('data-dd-preview', '')
  disarmPreview()
  inner.observe(dialog, { childList: true, subtree: true })
  refreshActive()
}

// Content inside a preview dialog renders late (async data, v-if) — keep marking close buttons.
const inner = new MutationObserver((records) => {
  for (const r of records) { const root = (r.target as Element).closest?.(PREVIEW_SELECTOR); if (root) markAllowed(root) }
})

const portals = new MutationObserver((records) => {
  for (const r of records) {
    for (const n of r.addedNodes) {
      if (!(n instanceof Element)) continue
      // Decide at the moment the dialog's portal appears — the arming window is short.
      const preview = isPreviewArmed() || !!document.querySelector(`.q-dialog${PREVIEW_SELECTOR}`)
      if (preview) tagWhenReady(n)
    }
    if (r.removedNodes.length) refreshActive()
  }
})

// A page opting in (`data-dd-preview` on its root) — attribute-only, so cheap.
const pages = new MutationObserver(() => refreshActive())

// ── 3. Safety net ────────────────────────────────────────────────────────────
type AnyFn = (...args: any[]) => any
const P = Parse.Object as unknown as Record<string, AnyFn> & { prototype: Record<string, AnyFn> }
const originals = { save: P.prototype.save, destroy: P.prototype.destroy, saveAll: P.saveAll, destroyAll: P.destroyAll }
function refuse() {
  notify()
  return Promise.reject(new Parse.Error(Parse.Error.OPERATION_FORBIDDEN, 'demo-read-only: preview mode'))
}
function installSafetyNet() {
  P.prototype.save = function (this: unknown, ...a: unknown[]) { return previewActive.value ? refuse() : originals.save!.apply(this, a) }
  P.prototype.destroy = function (this: unknown, ...a: unknown[]) { return previewActive.value ? refuse() : originals.destroy!.apply(this, a) }
  P.saveAll = (...a: unknown[]) => (previewActive.value ? refuse() : originals.saveAll!.apply(Parse.Object, a))
  P.destroyAll = (...a: unknown[]) => (previewActive.value ? refuse() : originals.destroyAll!.apply(Parse.Object, a))
}
function removeSafetyNet() {
  P.prototype.save = originals.save!; P.prototype.destroy = originals.destroy!
  P.saveAll = originals.saveAll!; P.destroyAll = originals.destroyAll!
}

async function startOwn() {
  await logout()
  await router.push({ path: '/login', query: { tab: 'signup' } })
}

const LISTENERS: [string, EventListener][] = [
  ['pointerdown', onPress], ['mousedown', onPress], ['touchstart', onPress],
  ['click', onClick], ['keydown', onKey as EventListener], ['focusin', onFocus as EventListener],
  ['beforeinput', onEdit], ['input', onEdit], ['change', onEdit], ['paste', onEdit], ['drop', onEdit], ['submit', onEdit],
]

onMounted(() => {
  for (const [type, fn] of LISTENERS) document.addEventListener(type, fn, { capture: true })
  portals.observe(document.body, { childList: true })
  const app = document.getElementById('q-app')
  if (app) pages.observe(app, { attributes: true, attributeFilter: ['data-dd-preview'], subtree: true, childList: false })
  router.afterEach(() => setTimeout(refreshActive, 0))
  installSafetyNet()
  refreshActive()
})
onBeforeUnmount(() => {
  for (const [type, fn] of LISTENERS) document.removeEventListener(type, fn, { capture: true })
  portals.disconnect(); inner.disconnect(); pages.disconnect()
  removeSafetyNet()
})
</script>

<style lang="scss">
/* Global on purpose: these rules style dialogs teleported out of any component's scope. */
.dd-preview-banner {
  position: fixed; top: 0; left: 0; right: 0; z-index: 7000;
  display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap;
  padding: calc(8px + env(safe-area-inset-top, 0px)) max(16px, env(safe-area-inset-right, 0px)) 8px max(16px, env(safe-area-inset-left, 0px));
  background: #e0f1f3; color: #14222b; font-size: 14px;
  box-shadow: 0 2px 12px rgba(10, 30, 40, 0.18);
}
body.body--dark .dd-preview-banner { background: #15343a; color: #e4edf1; }
/* Start every preview dialog below the banner — centred, side-panel and maximized alike. The
   inner box is fixed to the screen, so moving its top moves the whole dialog. */
.q-dialog[data-dd-preview] .q-dialog__inner { top: calc(52px + env(safe-area-inset-top, 0px)) !important; }
.q-dialog[data-dd-preview] .q-dialog__inner > div { max-height: calc(100vh - 52px - 48px - env(safe-area-inset-top, 0px)) !important; }
.q-dialog[data-dd-preview] .q-dialog__inner--maximized > div { max-height: calc(100vh - 52px - env(safe-area-inset-top, 0px)) !important; }

/* Greyed: the fields and the action buttons. Headings, text and layout stay in colour. */
[data-dd-preview] {
  .q-field, .q-toggle, .q-checkbox, .q-radio, .q-slider, .q-range, .q-option-group,
  .q-btn-toggle, .q-rating, .q-date, .q-time, .q-uploader, .q-editor, .q-color-picker,
  .q-btn:not(.dd-preview-allow):not(.q-tab) {
    opacity: 0.5;
    filter: grayscale(0.7);
    cursor: not-allowed;
  }
}
</style>
